<template>
  <section class="section-container">
    <h1 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h1>
    <div class="section-container-body fade-up-animation">
      <p>
        At Vortec, accessible from vortec.io, the privacy of our visitors is one of our highest priorities. This Privacy
        Policy explains what information we collect, why we collect it, and how we use it.
      </p>

      <p>
        If you have any questions or require more information about this Privacy Policy, please do not hesitate to
        contact us at <a href="mailto:hello@vortec.io">hello@vortec.io</a>.
      </p>

      <p>
        This Privacy Policy applies solely to our online activities and is valid for visitors to vortec.io with regard
        to the information they share with, or that we collect on, this website. It does not apply to any information
        collected offline or through channels other than this website.
      </p>

      <p><strong>Last updated:</strong> February 2025</p>

      <h4 class="heading-4">Consent</h4>

      <p>
        By using our website, you consent to this Privacy Policy and agree to its terms. Where we rely on consent to
        process your data (such as for analytics cookies), you may withdraw that consent at any time by adjusting your
        browser settings or contacting us.
      </p>

      <h4 class="heading-4">Information We Collect</h4>

      <p>
        We only collect personal information that is necessary to provide our services and improve your experience. The
        reasons for collecting any personal information will always be made clear at the point of collection.
      </p>
      <p>
        <strong>When you contact us directly</strong>, we may receive information such as your name, email address,
        phone number, company name, and the contents of your message or any attachments you choose to send.
      </p>
      <p>
        <strong>When you submit a form on our website</strong> (such as a contact or referral form), we collect the
        information you provide in that form, including your name, email address, and any details you choose to share.
      </p>
      <p>
        <strong>Automatically collected data</strong> — like most websites, we collect certain technical information
        automatically when you visit, including your IP address, browser type, device information, pages visited, and
        time spent on the site. This is collected via log files and analytics tools.
      </p>

      <h4 class="heading-4">How We Use Your Information</h4>

      <p>We use the information we collect to:</p>

      <ul style="margin-left: 20px">
        <li>Respond to your inquiries and provide the services you've requested</li>
        <li>Operate, maintain, and improve our website</li>
        <li>Understand how visitors use our website so we can enhance the experience</li>
        <li>Send relevant communications, including project updates and service information</li>
        <li>Detect and prevent fraud or misuse of our services</li>
        <li>Comply with legal obligations</li>
      </ul>

      <p>We do not sell, trade, or rent your personal information to third parties.</p>

      <h4 class="heading-4">Log Files</h4>

      <p>
        Like most websites, vortec.io uses server log files to record visitor activity. Log files may include IP
        addresses, browser type, Internet Service Provider (ISP), referring and exit pages, date and time stamps, and
        click counts. This data is not linked to personally identifiable information and is used solely for analyzing
        trends, administering the site, and monitoring overall performance.
      </p>

      <h4 class="heading-4">Cookies</h4>

      <p>
        Our website uses cookies — small text files stored on your device — to improve your browsing experience. When
        you first visit vortec.io, you are presented with a cookie consent notice. Analytics and tracking cookies are
        only loaded after you explicitly accept.
      </p>

      <p>
        For full details on the cookies we use and how to manage your preferences, please see our
        <nuxt-link to="/cookie-policy">Cookie Policy</nuxt-link>.
      </p>

      <h4 class="heading-4">Third-Party Services</h4>

      <p>
        We use a small number of trusted third-party services to help us understand how our website is used and to
        improve the experience we offer. These services may collect data about your visit in accordance with their own
        privacy policies:
      </p>

      <ul style="margin-left: 20px">
        <li>
          <strong>Google Analytics</strong> — We use Google Analytics to understand how visitors interact with our
          website. Google may collect data such as pages visited, time on site, and browser information. You can learn
          more at
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
            >Google's Privacy Policy</a
          >.
        </li>
        <li>
          <strong>ContentSquare</strong> — We use ContentSquare to analyze user behavior and improve our website
          experience. ContentSquare may collect interaction data such as clicks and scroll depth. You can learn more at
          <a href="https://contentsquare.com/privacy-center/" target="_blank" rel="noopener noreferrer"
            >ContentSquare's Privacy Center</a
          >.
        </li>
      </ul>

      <p>
        These services are only activated on our website after you have provided consent via our cookie notice. Vortec
        does not share personally identifiable information with these services beyond what is automatically collected as
        part of their standard operation.
      </p>

      <h4 class="heading-4">GDPR Data Protection Rights</h4>

      <p>
        If you are located in the European Economic Area (EEA), the United Kingdom, or North Macedonia, you have the
        following rights regarding your personal data:
      </p>
      <p><strong>Right of access</strong> — You may request a copy of the personal data we hold about you.</p>
      <p>
        <strong>Right to rectification</strong> — You may request that we correct inaccurate or incomplete information.
      </p>
      <p>
        <strong>Right to erasure</strong> — You may request that we delete your personal data, subject to certain
        conditions.
      </p>
      <p>
        <strong>Right to restrict processing</strong> — You may request that we limit how we process your data, under
        certain conditions.
      </p>
      <p>
        <strong>Right to object</strong> — You may object to our processing of your personal data, under certain
        conditions.
      </p>
      <p>
        <strong>Right to data portability</strong> — You may request that we provide your data in a structured,
        machine-readable format for transfer to another organization.
      </p>
      <p>
        We will respond to all valid requests within 30 days. To exercise any of these rights, please contact us at
        <a href="mailto:hello@vortec.io">hello@vortec.io</a>.
      </p>

      <h4 class="heading-4">CCPA Privacy Rights (California Residents)</h4>

      <p>
        If you are a California resident, you have the right to request disclosure of the categories and specific pieces
        of personal data we have collected about you, request deletion of your personal data, and opt out of the sale of
        your personal data. We do not sell personal data.
      </p>
      <p>
        To exercise your rights under the CCPA, please contact us at
        <a href="mailto:hello@vortec.io">hello@vortec.io</a>. We will respond within 30 days.
      </p>

      <h4 class="heading-4">Children's Privacy</h4>

      <p>
        Our website is not directed at children under the age of 13, and we do not knowingly collect personal
        information from children. If you believe a child has provided us with personal information, please contact us
        immediately at <a href="mailto:hello@vortec.io">hello@vortec.io</a> and we will promptly remove it from our
        records.
      </p>

      <h4 class="heading-4">Data Retention</h4>

      <p>
        We retain personal data only for as long as is necessary to fulfill the purposes outlined in this policy, or as
        required by law. Contact form submissions are retained for the duration of our business relationship and up to 3
        years thereafter. Analytics data is retained in accordance with the data retention settings of the respective
        third-party tools.
      </p>

      <h4 class="heading-4">Changes to This Privacy Policy</h4>

      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. Any
        updates will be posted on this page with a revised "Last updated" date. We encourage you to review this page
        periodically.
      </p>

      <h4 class="heading-4">Contact Us</h4>

      <p>
        If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us at
        <a href="mailto:hello@vortec.io">hello@vortec.io</a>.
      </p>
    </div>
  </section>
  <ClientOnly>
    <ContactForm />
  </ClientOnly>
</template>

<script setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';

const lines = [
  { text: 'Privacy', special: false },
  { text: 'Policy', special: true },
];

useFadeUpObserver();

useSeo({
  title: 'Privacy Policy | VORTEC',
  description: 'Learn about our privacy policy and how we use your data.',
  path: '/privacy-policy',
  jsonLd: {
    '@type': 'WebPage',
    name: 'Privacy Policy | VORTEC',
    description: 'Learn about our privacy policy and how we use your data.',
    url: 'https://vortec.io/privacy-policy',
  },
});
</script>
